import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import AdminPage from './AdminPage';
import type { 
  ConfigListResponse, 
  ConfigInfo 
} from '@services/config/types';

const AdminConfigListPage: React.FC = () => {
  const [configs, setConfigs] = useState<ConfigInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.post<ConfigListResponse>('/admin/config/list', {});
      setConfigs(response.configs);
    } catch (err) {
      console.error('Error loading configs:', err);
      setError('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleEditConfig = (configName: string) => {
    navigate(`/admin/configs/edit/${configName}`);
  };

  const formatLastModified = (date?: Date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AdminPage>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            System Configurations
          </Typography>
          <Button 
            variant="outlined" 
            onClick={loadConfigs}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Configuration Overview
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Manage system-wide configuration settings. Configurations use hardcoded defaults until customized.
              Click "Edit" to modify values or "Reset" to revert to defaults.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Configuration Name</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Last Modified</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configs.map((config) => (
                    <TableRow key={config.name} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {config.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {config.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={config.isCustomized ? 'Customized' : 'Using Defaults'}
                          color={config.isCustomized ? 'primary' : 'default'}
                          size="small"
                          variant={config.isCustomized ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatLastModified(config.lastModified)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleEditConfig(config.name)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {configs.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No configurations found.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminPage>
  );
};

export default AdminConfigListPage;